import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { formatApiError } from "@/utils/formatters";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Plus, Trash, PencilSimple, Key, ShieldStar } from "@phosphor-icons/react";

function PermBadges({ permissions }) {
  if (!permissions || permissions.length === 0) return <span className="text-[#737373] text-xs">None</span>;
  return (
    <div className="flex items-center gap-1 flex-wrap">
      {permissions.map((p) => (
        <Badge key={p} className="bg-[#262626] text-[#A3A3A3] rounded-sm text-[9px] uppercase tracking-wider border-0">{p}</Badge>
      ))}
    </div>
  );
}

function UserRow({ user, onDelete, onEdit, onResetPwd, selected, onToggleSelect, isSuperadmin, canResetPwd, currentUserId }) {
  const canDelete = isSuperadmin && !user.protected;
  const isSelf = currentUserId === user._id;
  // Show Reset-Password icon when:
  //   - caller is superadmin (can reset ANY user incl. protected + other superadmins)
  //   - OR caller has change_user_password perm and target is a non-superadmin
  // - Never for self (use Profile tab)
  const showResetPwd = !isSelf && canResetPwd && (isSuperadmin || user.role !== "superadmin");
  return (
    <TableRow className={`border-b border-[#1A1A1A] hover:bg-[#1A1A1A] ${user.hidden ? "opacity-60" : ""}`} data-testid={`user-row-${user._id}`}>
      <TableCell className="w-8">
        {canDelete ? (
          <Checkbox checked={selected} onCheckedChange={() => onToggleSelect(user._id)}
            className="border-[#262626] data-[state=checked]:bg-[#007AFF]" data-testid={`select-user-${user._id}`} />
        ) : <div className="w-4" />}
      </TableCell>
      <TableCell className="text-sm text-white">
        <div className="flex items-center gap-1.5">
          {user.name}
          {user.protected && <span className="px-1.5 py-0.5 rounded-sm text-[8px] font-bold uppercase bg-[#FFD60A]/20 text-[#FFD60A] border border-[#FFD60A]/40">Protected</span>}
          {user.hidden && <span className="px-1.5 py-0.5 rounded-sm text-[8px] font-bold uppercase bg-[#737373]/20 text-[#737373] border border-[#737373]/40">Hidden</span>}
        </div>
      </TableCell>
      <TableCell className="font-mono-data text-sm text-[#A3A3A3]">{user.username || user.email}</TableCell>
      <TableCell>
        <Badge className={`${user.role === "superadmin" ? "bg-[#007AFF]" : "bg-[#262626]"} text-white rounded-sm text-[10px] uppercase tracking-wider border-0`}>
          {user.role}
        </Badge>
      </TableCell>
      <TableCell className="text-sm text-[#A3A3A3]">{user.location || "\u2014"}</TableCell>
      <TableCell><PermBadges permissions={user.permissions} /></TableCell>
      <TableCell>
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="icon" onClick={() => onEdit(user)} className="text-[#007AFF] hover:text-[#007AFF] hover:bg-[#007AFF]/10" data-testid={`edit-user-${user._id}`}>
            <PencilSimple size={16} />
          </Button>
          {showResetPwd && (
            <Button variant="ghost" size="icon" onClick={() => onResetPwd(user)} className="text-[#FACC15] hover:text-[#FACC15] hover:bg-[#FACC15]/10" data-testid={`reset-pwd-${user._id}`} title="Reset password">
              <Key size={16} />
            </Button>
          )}
          {canDelete && (
            <Button variant="ghost" size="icon" onClick={() => onDelete(user._id, user.name)} className="text-[#FF3B30] hover:text-[#FF3B30] hover:bg-[#FF3B30]/10" data-testid={`delete-user-${user._id}`}>
              <Trash size={16} />
            </Button>
          )}
        </div>
      </TableCell>
    </TableRow>
  );
}

const INITIAL_FORM = { name: "", username: "", email: "", password: "", role: "user", location: "Lobby", permissions: [], default_page: "" };
const ALL_PERMISSIONS = [
  { key: "car_validation", label: "Car Validation (Validate Car page)" },
  { key: "validations", label: "Validations" },
  { key: "analytics", label: "Analytics" },
  { key: "users", label: "Users Management" },
  { key: "flagged", label: "Flagged Cases" },
  { key: "export", label: "Export" },
  { key: "settings", label: "System Settings" },
  { key: "audit", label: "Audit Log" },
  { key: "guest_registry", label: "Guest Registry" },
  { key: "interface", label: "Interface Setup" },
  { key: "security", label: "Security (Crew Check-In/Out)" },
  { key: "security_admin", label: "Security Admin (Upload PDF / Clear Data)" },
  { key: "key_management", label: "Key Management" },
  { key: "change_user_password", label: "Change User Passwords (includes Users tab)" },
];

// Available landing pages a user can be assigned to open by default after login.
// `validate` and `profile` are always available. Permission-gated pages are available if the user has that permission (enforced at login-time; here we surface all so admins can pick).
const DEFAULT_PAGE_OPTIONS = [
  { key: "validate", label: "Validate Car (default)", perm: "car_validation" },
  { key: "validations", label: "Validations", perm: "validations" },
  { key: "analytics", label: "Analytics", perm: "analytics" },
  { key: "users", label: "Users Management", perm: "users" },
  { key: "flagged", label: "Flagged Cases", perm: "flagged" },
  { key: "export", label: "Export", perm: "export" },
  { key: "settings", label: "Settings", perm: "settings" },
  { key: "audit", label: "Audit Log", perm: "audit" },
  { key: "guest-registry", label: "Guest Registry", perm: "guest_registry" },
  { key: "interface", label: "Interface Setup", perm: "interface" },
  { key: "security", label: "Security (Group Workers Validation)", perm: "security" },
  { key: "security-admin", label: "Security Admin", perm: "security_admin" },
  { key: "key-management", label: "Key Management", perm: "key_management" },
  { key: "profile", label: "My Profile", perm: null },
];

export function UsersTab() {
  const { user: currentUser } = useAuth();
  const isSuperadmin = currentUser?.role === "superadmin";
  const [users, setUsers] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState(INITIAL_FORM);
  const [adding, setAdding] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [editForm, setEditForm] = useState({ name: "", email: "", password: "", location: "", permissions: [], hidden: false, protected: false, default_page: "" });
  const [saving, setSaving] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [bulkDeleteConfirm, setBulkDeleteConfirm] = useState(false);
  const [showHidden, setShowHidden] = useState(false);
  const [resetPwdUser, setResetPwdUser] = useState(null);
  const [resetPwdValue, setResetPwdValue] = useState("");
  const [resetPwdShow, setResetPwdShow] = useState(true);
  const [resettingPwd, setResettingPwd] = useState(false);
  const [showAuditDialog, setShowAuditDialog] = useState(false);
  const [auditData, setAuditData] = useState(null);
  const [loadingAudit, setLoadingAudit] = useState(false);

  const canResetPwd = isSuperadmin || (currentUser?.permissions || []).includes("change_user_password");

  const loadUsers = useCallback(async () => {
    try {
      const { data } = await api.get(`/users?show_hidden=${showHidden}`);
      setUsers(data);
    } catch (err) {
      console.error(err);
    }
  }, [showHidden]);

  useEffect(() => { loadUsers(); }, [loadUsers]);

  const handleAdd = async () => {
    if (!form.name || !form.username || !form.password) return;
    setAdding(true);
    try {
      await api.post("/users", form);
      toast.success("User created");
      setShowAdd(false);
      setForm(INITIAL_FORM);
      loadUsers();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setAdding(false);
    }
  };

  const handleDelete = async (id, name) => {
    setDeleteConfirm({ id, name });
  };

  const confirmDelete = async () => {
    if (!deleteConfirm) return;
    try {
      await api.delete(`/users/${deleteConfirm.id}`);
      toast.success("User deleted");
      setSelectedIds((prev) => { const n = new Set(prev); n.delete(deleteConfirm.id); return n; });
      loadUsers();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setDeleteConfirm(null);
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const selectableUsers = users.filter((u) => isSuperadmin && !u.protected);
  const allSelected = selectableUsers.length > 0 && selectableUsers.every((u) => selectedIds.has(u._id));

  const toggleSelectAll = () => {
    if (allSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(selectableUsers.map((u) => u._id)));
    }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) return;
    setBulkDeleteConfirm(true);
  };

  const confirmBulkDelete = async () => {
    let deleted = 0;
    for (const id of selectedIds) {
      try {
        await api.delete(`/users/${id}`);
        deleted++;
      } catch (err) {
        console.error(`Failed to delete ${id}:`, err);
      }
    }
    toast.success(`Deleted ${deleted} user(s)`);
    setSelectedIds(new Set());
    setBulkDeleteConfirm(false);
    loadUsers();
  };

  const handleEditOpen = (user) => {
    setEditUser(user);
    setEditForm({ name: user.name, email: user.email || "", password: "", location: user.location || "", permissions: user.permissions || [], hidden: !!user.hidden, protected: !!user.protected, default_page: user.default_page || "" });
  };

  const toggleEditPerm = (key) => {
    setEditForm((prev) => {
      const hasKey = prev.permissions.includes(key);
      let newPerms = hasKey ? prev.permissions.filter((p) => p !== key) : [...prev.permissions, key];
      // change_user_password implies 'users' tab access — auto-add 'users' when granted
      if (!hasKey && key === "change_user_password" && !newPerms.includes("users")) {
        newPerms = [...newPerms, "users"];
      }
      return { ...prev, permissions: newPerms };
    });
  };

  const toggleAddPerm = (key) => {
    setForm((prev) => {
      const hasKey = prev.permissions.includes(key);
      let newPerms = hasKey ? prev.permissions.filter((p) => p !== key) : [...prev.permissions, key];
      if (!hasKey && key === "change_user_password" && !newPerms.includes("users")) {
        newPerms = [...newPerms, "users"];
      }
      return { ...prev, permissions: newPerms };
    });
  };

  const handleEditSave = async () => {
    if (!editUser) return;
    const payload = {};
    if (editForm.name.trim() && editForm.name !== editUser.name) payload.name = editForm.name.trim();
    if (isSuperadmin && editForm.email.trim() && editForm.email.trim().toLowerCase() !== (editUser.email || "").toLowerCase()) payload.email = editForm.email.trim();
    if (editForm.location !== (editUser.location || "")) payload.location = editForm.location;
    if (editForm.password.trim()) payload.password = editForm.password;
    // Always send permissions if superadmin
    if (isSuperadmin) {
      const oldPerms = (editUser.permissions || []).sort().join(",");
      const newPerms = editForm.permissions.sort().join(",");
      if (oldPerms !== newPerms) payload.permissions = editForm.permissions;
      if (editForm.hidden !== !!editUser.hidden) payload.hidden = editForm.hidden;
      if (editForm.protected !== !!editUser.protected) payload.protected = editForm.protected;
    }
    if ((editForm.default_page || "") !== (editUser.default_page || "")) {
      payload.default_page = editForm.default_page || "";
    }
    if (Object.keys(payload).length === 0) {
      setEditUser(null);
      return;
    }
    setSaving(true);
    try {
      await api.put(`/users/${editUser._id}`, payload);
      toast.success("User updated");
      setEditUser(null);
      loadUsers();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <p className="text-sm text-[#A3A3A3]">{users.length} users</p>
          {selectedIds.size > 0 && (
            <Button onClick={handleBulkDelete} size="sm"
              className="bg-[#FF3B30] hover:bg-[#FF3B30]/80 text-white rounded-sm text-xs" data-testid="bulk-delete-btn">
              <Trash size={14} className="mr-1" /> Delete {selectedIds.size} Selected
            </Button>
          )}
          {isSuperadmin && (
            <div className="flex items-center gap-2">
              <Checkbox checked={showHidden} onCheckedChange={(v) => setShowHidden(!!v)}
                className="border-[#262626] data-[state=checked]:bg-[#007AFF]" data-testid="show-hidden-toggle" />
              <span className="text-xs text-[#737373]">Show Hidden</span>
            </div>
          )}
        </div>
        <Button onClick={() => setShowAdd(true)} className="bg-[#007AFF] hover:bg-[#3395FF] text-white rounded-sm text-xs uppercase tracking-wider" data-testid="add-user-btn">
          <Plus size={16} className="mr-1" /> Add User
        </Button>
      </div>

      {/* Permissions Audit button row */}
      <div className="flex items-center justify-end -mt-2">
        <Button
          variant="ghost" size="sm"
          onClick={async () => {
            setShowAuditDialog(true);
            setLoadingAudit(true);
            try {
              const { data } = await api.get("/users/permissions-audit");
              setAuditData(data);
            } catch (err) {
              toast.error(formatApiError(err.response?.data?.detail) || "Failed to load audit");
            } finally {
              setLoadingAudit(false);
            }
          }}
          className="text-[#FACC15] hover:text-[#FACC15] hover:bg-[#FACC15]/10 text-[10px] uppercase tracking-[0.15em] gap-1.5"
          data-testid="open-perm-audit-btn"
        >
          <ShieldStar size={12} weight="bold" /> Permissions Audit
        </Button>
      </div>

      <Table>
        <TableHeader>
          <TableRow className="border-b border-[#262626] hover:bg-transparent">
            <TableHead className="w-8">
              <Checkbox checked={allSelected && selectableUsers.length > 0}
                onCheckedChange={toggleSelectAll}
                className="border-[#262626] data-[state=checked]:bg-[#007AFF]" data-testid="select-all-users" />
            </TableHead>
            <TableHead className="text-xs uppercase tracking-[0.15em] text-[#737373]">Name</TableHead>
            <TableHead className="text-xs uppercase tracking-[0.15em] text-[#737373]">Username</TableHead>
            <TableHead className="text-xs uppercase tracking-[0.15em] text-[#737373]">Role</TableHead>
            <TableHead className="text-xs uppercase tracking-[0.15em] text-[#737373]">Location</TableHead>
            <TableHead className="text-xs uppercase tracking-[0.15em] text-[#737373]">Permissions</TableHead>
            <TableHead className="text-xs uppercase tracking-[0.15em] text-[#737373]">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {users.map((u) => (
            <UserRow key={u._id} user={u} onDelete={handleDelete} onEdit={handleEditOpen}
              onResetPwd={(usr) => { setResetPwdUser(usr); setResetPwdValue(""); setResetPwdShow(true); }}
              selected={selectedIds.has(u._id)} onToggleSelect={toggleSelect}
              isSuperadmin={isSuperadmin} canResetPwd={canResetPwd} currentUserId={currentUser?.id} />
          ))}
        </TableBody>
      </Table>

      {/* Add User Dialog */}
      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent className="bg-[#121212] border-[#262626] rounded-sm">
          <DialogHeader>
            <DialogTitle className="font-heading text-lg text-white">ADD NEW USER</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-[0.15em] text-[#A3A3A3]">Name</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-[#0A0A0A] border-[#262626] text-white rounded-sm" data-testid="new-user-name" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-[0.15em] text-[#A3A3A3]">Username</Label>
              <Input value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} className="bg-[#0A0A0A] border-[#262626] text-white rounded-sm" data-testid="new-user-username" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-[0.15em] text-[#A3A3A3]">Email <span className="normal-case text-[#737373]">(optional)</span></Label>
              <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="bg-[#0A0A0A] border-[#262626] text-white rounded-sm" data-testid="new-user-email" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-[0.15em] text-[#A3A3A3]">Password</Label>
              <Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} autoComplete="new-password" data-lpignore="true" data-form-type="other" name="add-user-pw" className="bg-[#0A0A0A] border-[#262626] text-white rounded-sm" data-testid="new-user-password" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-[0.15em] text-[#A3A3A3]">Role</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger className="bg-[#0A0A0A] border-[#262626] text-white rounded-sm" data-testid="new-user-role"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#121212] border-[#262626]">
                  <SelectItem value="user">Staff</SelectItem>
                  <SelectItem value="superadmin">Superadmin</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-[0.15em] text-[#A3A3A3]">Location</Label>
              <Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} className="bg-[#0A0A0A] border-[#262626] text-white rounded-sm" data-testid="new-user-location" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-[0.15em] text-[#A3A3A3]">Default Page <span className="normal-case text-[#737373]">(page shown after login)</span></Label>
              <Select value={form.default_page || "validate"} onValueChange={(v) => setForm({ ...form, default_page: v === "validate" ? "" : v })}>
                <SelectTrigger className="bg-[#0A0A0A] border-[#262626] text-white rounded-sm" data-testid="new-user-default-page"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#121212] border-[#262626]">
                  {DEFAULT_PAGE_OPTIONS.map((o) => (
                    <SelectItem key={o.key} value={o.key}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {isSuperadmin && (
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-[0.15em] text-[#A3A3A3]">Tab Permissions</Label>
                {ALL_PERMISSIONS.map((p) => (
                  <div key={p.key} className="flex items-center gap-2">
                    <Checkbox
                      checked={form.permissions.includes(p.key)}
                      onCheckedChange={() => toggleAddPerm(p.key)}
                      data-testid={`new-user-perm-${p.key}`}
                      className="border-[#262626] data-[state=checked]:bg-[#007AFF]"
                    />
                    <span className="text-sm text-[#A3A3A3]">{p.label}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAdd(false)} className="border-[#262626] text-[#A3A3A3] hover:bg-[#1A1A1A] hover:text-white rounded-sm">Cancel</Button>
            <Button onClick={handleAdd} disabled={adding} className="bg-[#007AFF] hover:bg-[#3395FF] text-white rounded-sm" data-testid="confirm-add-user-btn">
              {adding ? "Creating..." : "Create User"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog open={!!editUser} onOpenChange={(open) => { if (!open) setEditUser(null); }}>
        <DialogContent className="bg-[#121212] border-[#262626] rounded-sm">
          <DialogHeader>
            <DialogTitle className="font-heading text-lg text-white">EDIT USER</DialogTitle>
          </DialogHeader>
          {editUser && (
            <div className="space-y-3">
              <p className="text-xs text-[#737373]">Username: <span className="font-mono-data text-white">{editUser.username || editUser.email}</span></p>
              {isSuperadmin && (
                <div className="space-y-1.5">
                  <Label className="text-xs uppercase tracking-[0.15em] text-[#A3A3A3]">Email <span className="normal-case text-[#737373]">(optional)</span></Label>
                  <Input type="email" value={editForm.email} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} className="bg-[#0A0A0A] border-[#262626] text-white rounded-sm font-mono-data" data-testid="edit-user-email" />
                </div>
              )}
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-[0.15em] text-[#A3A3A3]">Name</Label>
                <Input value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} className="bg-[#0A0A0A] border-[#262626] text-white rounded-sm" data-testid="edit-user-name" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-[0.15em] text-[#A3A3A3]">Location</Label>
                <Input value={editForm.location} onChange={(e) => setEditForm({ ...editForm, location: e.target.value })} className="bg-[#0A0A0A] border-[#262626] text-white rounded-sm" data-testid="edit-user-location" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-[0.15em] text-[#A3A3A3]">Default Page <span className="normal-case text-[#737373]">(page shown after login)</span></Label>
                <Select value={editForm.default_page || "validate"} onValueChange={(v) => setEditForm({ ...editForm, default_page: v === "validate" ? "" : v })}>
                  <SelectTrigger className="bg-[#0A0A0A] border-[#262626] text-white rounded-sm" data-testid="edit-user-default-page"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#121212] border-[#262626]">
                    {DEFAULT_PAGE_OPTIONS.map((o) => (
                      <SelectItem key={o.key} value={o.key}>{o.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-[0.15em] text-[#A3A3A3]">New Password <span className="normal-case text-[#737373]">(leave blank to keep current)</span></Label>
                <Input type="password" value={editForm.password} onChange={(e) => setEditForm({ ...editForm, password: e.target.value })} placeholder="Enter new password" autoComplete="new-password" data-lpignore="true" data-form-type="other" name="edit-user-pw" className="bg-[#0A0A0A] border-[#262626] text-white rounded-sm placeholder:text-[#737373]" data-testid="edit-user-password" />
              </div>
              {isSuperadmin && editUser.role !== "superadmin" && (
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-[0.15em] text-[#A3A3A3]">Tab Permissions</Label>
                  <p className="text-xs text-[#737373]">Grant this user access to admin tabs</p>
                  {ALL_PERMISSIONS.map((p) => (
                    <div key={p.key} className="flex items-center gap-2">
                      <Checkbox
                        checked={editForm.permissions.includes(p.key)}
                        onCheckedChange={() => toggleEditPerm(p.key)}
                        data-testid={`edit-user-perm-${p.key}`}
                        className="border-[#262626] data-[state=checked]:bg-[#007AFF]"
                      />
                      <span className="text-sm text-[#A3A3A3]">{p.label}</span>
                    </div>
                  ))}
                </div>
              )}
              {isSuperadmin && (
                <div className="space-y-2 pt-2 border-t border-[#262626]">
                  <Label className="text-xs uppercase tracking-[0.15em] text-[#A3A3A3]">Account Options</Label>
                  <div className="flex items-center gap-2">
                    <Checkbox checked={editForm.hidden} onCheckedChange={(v) => setEditForm({ ...editForm, hidden: !!v })}
                      className="border-[#262626] data-[state=checked]:bg-[#737373]" data-testid="edit-user-hidden" />
                    <span className="text-sm text-[#A3A3A3]">Hidden from user list</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Checkbox checked={editForm.protected} onCheckedChange={(v) => setEditForm({ ...editForm, protected: !!v })}
                      className="border-[#262626] data-[state=checked]:bg-[#FFD60A]" data-testid="edit-user-protected" />
                    <span className="text-sm text-[#A3A3A3]">Protected (cannot be deleted)</span>
                  </div>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditUser(null)} className="border-[#262626] text-[#A3A3A3] hover:bg-[#1A1A1A] hover:text-white rounded-sm">Cancel</Button>
            <Button onClick={handleEditSave} disabled={saving} className="bg-[#007AFF] hover:bg-[#3395FF] text-white rounded-sm" data-testid="confirm-edit-user-btn">
              {saving ? "Saving..." : "Save Changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteConfirm} onOpenChange={(open) => { if (!open) setDeleteConfirm(null); }}>
        <DialogContent className="bg-[#121212] border-[#262626] rounded-sm">
          <DialogHeader>
            <DialogTitle className="font-heading text-lg text-[#FF3B30]">DELETE USER</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-[#A3A3A3]">
            Are you sure you want to delete <span className="text-white font-semibold">{deleteConfirm?.name}</span>? This action cannot be undone.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteConfirm(null)} className="border-[#262626] text-[#A3A3A3] hover:bg-[#1A1A1A] hover:text-white rounded-sm">Cancel</Button>
            <Button onClick={confirmDelete} className="bg-[#FF3B30] hover:bg-[#FF3B30]/80 text-white rounded-sm" data-testid="confirm-delete-user-btn">
              <Trash size={14} className="mr-1" /> Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Delete Confirmation Dialog */}
      <Dialog open={bulkDeleteConfirm} onOpenChange={(open) => { if (!open) setBulkDeleteConfirm(false); }}>
        <DialogContent className="bg-[#121212] border-[#262626] rounded-sm">
          <DialogHeader>
            <DialogTitle className="font-heading text-lg text-[#FF3B30]">DELETE SELECTED USERS</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-[#A3A3A3]">
            Are you sure you want to delete <span className="text-white font-semibold">{selectedIds.size}</span> selected user(s)? This action cannot be undone.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkDeleteConfirm(false)} className="border-[#262626] text-[#A3A3A3] hover:bg-[#1A1A1A] hover:text-white rounded-sm">Cancel</Button>
            <Button onClick={confirmBulkDelete} className="bg-[#FF3B30] hover:bg-[#FF3B30]/80 text-white rounded-sm" data-testid="confirm-bulk-delete-btn">
              <Trash size={14} className="mr-1" /> Delete {selectedIds.size} User(s)
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog open={!!resetPwdUser} onOpenChange={(open) => { if (!open) { setResetPwdUser(null); setResetPwdValue(""); } }}>
        <DialogContent className="bg-[#121212] border-[#262626] rounded-sm" data-testid="reset-pwd-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-lg text-[#FACC15] flex items-center gap-2">
              <Key size={18} weight="bold" /> RESET PASSWORD
            </DialogTitle>
          </DialogHeader>
          {resetPwdUser && (
            <div className="space-y-3">
              <div className="bg-[#0A0A0A] border border-[#262626] rounded-sm p-3">
                <p className="text-xs text-[#737373]">User</p>
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="text-sm text-white">{resetPwdUser.name} <span className="font-mono-data text-[#A3A3A3] text-xs">({resetPwdUser.username})</span></p>
                  {resetPwdUser.role === "superadmin" && <span className="px-1.5 py-0.5 rounded-sm text-[8px] font-bold uppercase bg-[#007AFF]/20 text-[#007AFF] border border-[#007AFF]/40">Superadmin</span>}
                  {resetPwdUser.protected && <span className="px-1.5 py-0.5 rounded-sm text-[8px] font-bold uppercase bg-[#FFD60A]/20 text-[#FFD60A] border border-[#FFD60A]/40">Protected</span>}
                </div>
              </div>
              {(resetPwdUser.role === "superadmin" || resetPwdUser.protected) && (
                <div className="bg-[#FF3B30]/10 border border-[#FF3B30]/30 rounded-sm p-2 text-[10px] text-[#FF3B30]">
                  ⚠ You are resetting the password of a {resetPwdUser.role === "superadmin" ? "SUPERADMIN" : "PROTECTED"} account. Proceed with caution — this action is logged.
                </div>
              )}
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-[0.15em] text-[#A3A3A3]">New Password</Label>
                <div className="flex items-center gap-2">
                  <Input type={resetPwdShow ? "text" : "password"} value={resetPwdValue}
                    onChange={(e) => setResetPwdValue(e.target.value)}
                    placeholder="At least 6 characters" autoFocus
                    autoComplete="new-password" autoCorrect="off" spellCheck={false}
                    name="reset-target-password" data-lpignore="true" data-form-type="other"
                    className="bg-[#0A0A0A] border-[#262626] text-white rounded-sm flex-1 font-mono-data" data-testid="reset-pwd-input" />
                  <Button type="button" variant="ghost" size="sm" onClick={() => setResetPwdShow(!resetPwdShow)}
                    className="text-[#737373] hover:text-white text-xs">
                    {resetPwdShow ? "Hide" : "Show"}
                  </Button>
                </div>
              </div>
              {resetPwdValue && (
                <div className="bg-[#FACC15]/10 border border-[#FACC15]/30 rounded-sm p-2 text-[10px] text-[#FACC15]">
                  Confirm: <span className="font-mono-data font-bold">{resetPwdValue}</span> ({resetPwdValue.length} chars) — give this exact password to {resetPwdUser?.name}.
                </div>
              )}
              <p className="text-[10px] text-[#737373]">The user will need to use this new password on their next login. Action will be recorded in the audit log.</p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setResetPwdUser(null); setResetPwdValue(""); }} className="border-[#262626] text-[#A3A3A3] hover:bg-[#1A1A1A] hover:text-white rounded-sm">Cancel</Button>
            <Button onClick={async () => {
              if (!resetPwdUser) return;
              if (resetPwdValue.length < 6) { toast.error("Password must be at least 6 characters"); return; }
              setResettingPwd(true);
              try {
                await api.post(`/users/${resetPwdUser._id}/password`, { new_password: resetPwdValue });
                toast.success(`Password reset for ${resetPwdUser.username}`);
                setResetPwdUser(null);
                setResetPwdValue("");
              } catch (err) {
                toast.error(formatApiError(err.response?.data?.detail) || "Reset failed");
              } finally {
                setResettingPwd(false);
              }
            }} disabled={resettingPwd || resetPwdValue.length < 6} className="bg-[#FACC15] hover:bg-[#FACC15]/80 text-black rounded-sm font-semibold" data-testid="confirm-reset-pwd-btn">
              <Key size={14} className="mr-1" /> {resettingPwd ? "Resetting..." : "Reset Password"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Permissions Audit Dialog */}
      <Dialog open={showAuditDialog} onOpenChange={(open) => { if (!open) { setShowAuditDialog(false); setAuditData(null); } }}>
        <DialogContent className="bg-[#121212] border-[#262626] rounded-sm max-w-3xl" data-testid="perm-audit-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-lg text-[#FACC15] flex items-center gap-2">
              <ShieldStar size={18} weight="bold" /> PERMISSIONS AUDIT
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-[#737373]">
              Operators who can reset other users&apos; passwords. Use this for periodic security reviews.
            </p>
            {loadingAudit ? (
              <p className="text-sm text-[#A3A3A3]">Loading…</p>
            ) : auditData && auditData.operators ? (
              <div className="border border-[#262626] rounded-sm overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow className="border-b border-[#262626] hover:bg-transparent bg-[#0A0A0A]">
                      <TableHead className="text-[10px] uppercase tracking-[0.15em] text-[#737373]">Operator</TableHead>
                      <TableHead className="text-[10px] uppercase tracking-[0.15em] text-[#737373]">Role</TableHead>
                      <TableHead className="text-[10px] uppercase tracking-[0.15em] text-[#737373] text-right">Total Resets</TableHead>
                      <TableHead className="text-[10px] uppercase tracking-[0.15em] text-[#737373]">Last Used</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {auditData.operators.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={4} className="text-sm text-[#737373] text-center py-6">No privileged operators found.</TableCell>
                      </TableRow>
                    ) : auditData.operators.map((op) => (
                      <TableRow key={op.user_id} className="border-b border-[#1A1A1A] hover:bg-[#1A1A1A]" data-testid={`audit-row-${op.user_id}`}>
                        <TableCell className="text-sm text-white">
                          {op.name} <span className="font-mono-data text-[#A3A3A3] text-xs">({op.username})</span>
                        </TableCell>
                        <TableCell>
                          <Badge className={`${op.role === "superadmin" ? "bg-[#007AFF]" : "bg-[#FACC15]/30 text-[#FACC15]"} rounded-sm text-[10px] uppercase tracking-wider border-0`}>
                            {op.role === "superadmin" ? "Superadmin" : "Operator"}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-sm text-white text-right font-mono-data">
                          {op.total_resets || 0}
                        </TableCell>
                        <TableCell className="text-xs text-[#A3A3A3]">
                          {op.last_used ? (
                            <div className="flex flex-col">
                              <span className="font-mono-data">{new Date(op.last_used).toLocaleString()}</span>
                              {op.last_target && <span className="text-[10px] text-[#737373] truncate max-w-md" title={op.last_target}>{op.last_target}</span>}
                            </div>
                          ) : (
                            <span className="text-[#737373] italic">Never used</span>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            ) : (
              <p className="text-sm text-[#737373]">No data.</p>
            )}
            {auditData && (
              <p className="text-[10px] text-[#737373]">Showing {auditData.total} privileged operator(s). Data from audit logs.</p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowAuditDialog(false); setAuditData(null); }} className="border-[#262626] text-[#A3A3A3] hover:bg-[#1A1A1A] hover:text-white rounded-sm">Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
